import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download, Loader2 } from "lucide-react";
import { hijriString, gregorianString } from "../lib/api";
import html2pdf from "html2pdf.js";
import { useAuth } from "../contexts/AuthContext";
import { pickDefaultSchool } from "../lib/autoSelect";
import { localName, localFullName } from "../lib/localName";

export default function ReportCardPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [classId, setClassId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [term, setTerm] = useState("T1");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (isParent) {
      api.get("/my/children").then(r => {
        const kids = r.data || [];
        setStudents(kids);
        if (kids.length && !studentId) setStudentId(kids[0].id);
      });
    } else if (isStudent) {
      api.get("/my/student-record").then(r => {
        const s = r.data;
        if (s) { setStudents([s]); setStudentId(s.id); }
      });
    }
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      if (!isPersonal) setSchoolId(prev => prev || pickDefaultSchool(list, user));
    }).catch(() => setSchools([]));
  }, [isParent, isStudent]);

  // Staff: classes of selected school (auto-select first) → students of selected class
  useEffect(() => {
    if (isPersonal || !schoolId) return;
    api.get(`/classes?school_id=${schoolId}`).then(r => {
      const list = r.data || [];
      setClasses(list);
      setClassId(prev => (prev && list.some(c => c.id === prev)) ? prev : (list[0]?.id || ""));
    });
  }, [schoolId, isPersonal]);
  useEffect(() => {
    if (isPersonal || !classId) return;
    setStudentId("");
    setData(null);
    api.get(`/students?class_id=${classId}`).then(r => setStudents(r.data || []));
  }, [classId, isPersonal]);

  useEffect(() => {
    if (studentId) {
      api.get(`/report-card/${studentId}?term=${term}`).then(r => setData(r.data));
      const stu = students.find(s => s.id === studentId);
      if (stu) api.get(`/subjects?school_id=${stu.school_id}`).then(r => setSubjects(r.data || []));
    }
  }, [studentId, term]);

  const school = data?.student ? schools.find(s => s.id === data.student.school_id) : null;
  const logoUrl = school?.logo_id ? `${api.defaults.baseURL}/files/${school.logo_id}` : null;

  const exportPdf = async () => {
    if (!printRef.current) return;
    setBusy(true);
    try {
      await html2pdf().from(printRef.current).set({
        margin: 12,
        filename: `report-card-${data?.student?.full_name || data?.student?.full_name_ar || "student"}-${term}.pdf`,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }).save();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-semibold">{t("reportCard")}</h1>
        <Button onClick={exportPdf} disabled={!data || busy} data-testid="rc-export">
          {busy ? <Loader2 className="w-4 h-4 me-2 animate-spin"/> : <Download className="w-4 h-4 me-2"/>}
          PDF
        </Button>
      </div>

      <Card className="p-4 border shadow-none flex gap-3 flex-wrap items-center">
        {isPersonal ? (
          isParent && students.length > 1 ? (
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger className="w-72" data-testid="rc-child-select"><SelectValue placeholder={t("select_child")} /></SelectTrigger>
              <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
            </Select>
          ) : students.length === 1 ? (
            <span className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md">
              {students[0].full_name_ar || students[0].full_name}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{t("no_linked_children")}</span>
          )
        ) : (
          <>
            <Select value={schoolId} onValueChange={setSchoolId}>
              <SelectTrigger className="w-56" data-testid="rc-school"><SelectValue placeholder={t("select_school")}/></SelectTrigger>
              <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="w-48" data-testid="rc-class"><SelectValue placeholder={t("select_class")}/></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger className="w-72" data-testid="rc-student"><SelectValue placeholder={t("students")}/></SelectTrigger>
              <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="w-56" data-testid="rc-term-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="T1">{t("term_t1")}</SelectItem>
            <SelectItem value="T2">{t("term_t2")}</SelectItem>
            <SelectItem value="T3">{t("term_t3")}</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {data && data.student && (
        <div ref={printRef} className="bg-white text-black p-8 sm:p-12 rounded-lg border border-border shadow-none" dir={lang === "ar" ? "rtl" : "ltr"}>
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-300 pb-6 mb-8 gap-4">
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" className="w-16 h-16 object-contain" crossOrigin="anonymous"/>
              ) : (
                <div className="w-16 h-16 rounded-lg bg-emerald-700 text-white grid place-items-center font-bold text-lg">SI</div>
              )}
              <div>
                <div className="font-bold text-lg">{localName(school, lang) || "SCHOOLIN AI"}</div>
                <div className="text-xs text-gray-500">{school?.city}</div>
              </div>
            </div>
            <div className="text-end text-xs text-gray-500">
              <div className="font-mono">{hijriString()}</div>
              <div className="font-mono">{gregorianString(new Date(), lang === "ar" ? "ar-SA" : "en-GB")}</div>
            </div>
          </div>

          {/* Student */}
          <div className="flex items-end justify-between mb-8 gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{t("reportCard")}</div>
              <div className="text-3xl font-semibold mt-2">{data.student.full_name_ar || data.student.full_name}</div>
              <div className="text-sm text-gray-500 mt-1">National ID: <span className="font-mono">{data.student.national_id || "—"}</span></div>
              <div className="text-sm text-gray-500">{t("term")}: <span className="font-medium text-foreground">{t(`term_${data.term.toLowerCase()}`)}</span></div>
            </div>
            <div className="text-end">
              <div className="text-xs uppercase tracking-wider text-gray-500">{t("overall")}</div>
              <div className="text-5xl font-semibold text-emerald-700 mt-1" data-testid="rc-overall">{data.overall}</div>
            </div>
          </div>

          {/* Grades table */}
          <table className="w-full text-sm border-collapse mb-8">
            <thead>
              <tr className="border-y-2 border-gray-300">
                <th className="text-start px-3 py-2 text-xs uppercase tracking-wider text-gray-500">{t("subjects")}</th>
                <th className="text-start px-3 py-2 text-xs uppercase tracking-wider text-gray-500">Items</th>
                <th className="text-end px-3 py-2 text-xs uppercase tracking-wider text-gray-500">{t("average")}</th>
              </tr>
            </thead>
            <tbody>
              {data.subjects.map(s => (
                <tr key={s.subject_id} className="border-b border-gray-200" data-testid={`rc-subj-${s.subject_id}`}>
                  <td className="px-3 py-3 font-medium">{subjects.find(x => x.id === s.subject_id)?.name_ar || subjects.find(x => x.id === s.subject_id)?.name || s.subject_id.slice(0, 8)}</td>
                  <td className="px-3 py-3 text-gray-500">{s.items.length}</td>
                  <td className="px-3 py-3 text-end font-mono font-semibold">{s.average}</td>
                </tr>
              ))}
              {data.subjects.length === 0 && <tr><td colSpan={3} className="px-3 py-12 text-center text-gray-400">{t("no_data")}</td></tr>}
            </tbody>
          </table>

          {/* Signatures */}
          <div className="grid grid-cols-3 gap-6 mt-16 pt-6 border-t border-gray-300">
            {[
              lang === "ar" ? "مدير المدرسة" : "School Principal",
              lang === "ar" ? "رئيس الشؤون الأكاديمية" : "Head of Academic Affairs",
              lang === "ar" ? "ولي الأمر" : "Parent / Guardian",
            ].map((role, i) => (
              <div key={i} className="text-center" data-testid={`rc-signature-${i}`}>
                <div className="border-b border-gray-400 h-10 mb-2"></div>
                <div className="text-xs text-gray-500">{role}</div>
              </div>
            ))}
          </div>

          <div className="text-xs text-gray-400 text-center mt-8">SCHOOLIN AI · سكول إن</div>
        </div>
      )}
    </div>
  );
}
